/**
 * Central date formatting for CKD Companion.
 *
 * Beta finding: native <input type="date"> controls render in the browser
 * locale with no format label, so "09/07/2026" was read by a US tester as
 * September 7 — silently mis-dating clinical records. The product answer is a
 * per-user display preference (User.date_format) applied everywhere.
 *
 * IMPORTANT — presentation only. Storage, APIs, comparisons, CSV export, and
 * input `value`/`max` attributes all stay ISO `yyyy-MM-dd` end to end. Nothing
 * in here touches ordering or timezone logic; it converts an ISO date string to
 * a human string for display.
 *
 * The four supported preferences (kept in sync with backend schemas/auth.py):
 *   'mdy'          → 07/09/2026   (US numeric)
 *   'dmy'          → 09/07/2026   (intl numeric)
 *   'ymd'          → 2026-07-09   (ISO)
 *   'dd-mon-yyyy'  → 9 Jul 2026   (written month — default, ambiguity-proof)
 */
import { format } from 'date-fns';
import { useAuthStore } from '../stores/authStore';
import { formatDateTokens } from './format';

export type DateFormatPref = 'mdy' | 'dmy' | 'ymd' | 'dd-mon-yyyy';

/** Safe, ambiguity-proof fallback for unauthenticated / missing preferences. */
export const DEFAULT_DATE_FORMAT: DateFormatPref = 'dd-mon-yyyy';

/** Written-month tokens, used for unambiguous (vet-facing) rendering. */
const UNAMBIGUOUS_TOKEN = 'd MMM yyyy'; // "9 Jul 2026"

/**
 * Visual density of a formatted date.
 *  - 'full' → complete date honouring the preference (default).
 *  - 'long' → written-month with full month name ("9 July 2026").
 *
 * There is deliberately NO year-less style. One used to exist for chart ticks and
 * week headings, and it is what produced "WEEK OF 07-27" and an axis reading
 * "07-22 … 08-01": a date with no year, in an order the reader has to already
 * know. Every date this module renders carries enough to be read correctly by
 * someone who has never seen the setting.
 */
export type DateStyle = 'full' | 'long';

const FULL_TOKENS: Record<DateFormatPref, string> = {
  mdy: 'MM/dd/yyyy',
  dmy: 'dd/MM/yyyy',
  ymd: 'yyyy-MM-dd',
  'dd-mon-yyyy': UNAMBIGUOUS_TOKEN,
};

function tokensFor(pref: DateFormatPref, style: DateStyle): string {
  if (style === 'long') return 'd MMMM yyyy';
  return FULL_TOKENS[pref] ?? FULL_TOKENS[DEFAULT_DATE_FORMAT];
}

/**
 * Parse a date-only `yyyy-MM-dd` string into a LOCAL `Date` from its parts.
 *
 * Never routes a date-only string through `new Date('2026-07-09')`, which the
 * spec parses as UTC midnight and can render as the *previous* day in
 * negative-offset zones. A full ISO datetime (`...Thh:mm:ss...`) is tolerated —
 * only the leading date is used. Returns null for empty/invalid input, and
 * rejects impossible dates (e.g. `2026-02-30`) that JS would otherwise roll
 * over into the next month.
 */
export function parseISODateOnly(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const datePart = iso.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return null;
  }
  return dt;
}

/** The current user's stored preference, or the safe default when unauthenticated. */
export function getCurrentDateFormat(): DateFormatPref {
  const pref = useAuthStore.getState().user?.date_format;
  return isDateFormatPref(pref) ? pref : DEFAULT_DATE_FORMAT;
}

export function isDateFormatPref(value: unknown): value is DateFormatPref {
  return value === 'mdy' || value === 'dmy' || value === 'ymd' || value === 'dd-mon-yyyy';
}

export interface FormatDateOptions {
  /** Visual density — defaults to 'full'. */
  style?: DateStyle;
  /** Returned for null / empty / unparseable input. Defaults to ''. */
  fallback?: string;
}

/**
 * Format an ISO date-only string per the CURRENT user's preference.
 * Falls back to the ambiguity-proof default when unauthenticated or the stored
 * value is missing/unknown.
 */
export function formatDate(iso: string | null | undefined, options: FormatDateOptions = {}): string {
  const { style = 'full', fallback = '' } = options;
  const d = parseISODateOnly(iso);
  if (!d) return fallback;
  return formatDateTokens(d, tokensFor(getCurrentDateFormat(), style));
}

/**
 * Format an ISO date with an EXPLICIT preference (not the current user's).
 * Used by the signup/settings selector to render a live example of each option.
 */
export function formatDateWith(
  iso: string | null | undefined,
  pref: DateFormatPref,
  style: DateStyle = 'full',
): string {
  const d = parseISODateOnly(iso);
  if (!d) return '';
  return formatDateTokens(d, tokensFor(pref, style));
}

/**
 * Always render the written-month form ("9 Jul 2026") regardless of preference.
 *
 * Reserved for documents that LEAVE the app — the vet prep sheet and any future
 * print artefact of the same kind. The owner's date-format preference expresses
 * what the OWNER finds readable; a vet reading the printed sheet has no access to
 * it, and a numeric date on a document passing between two people is exactly the
 * ambiguity the written-month form exists to remove. See DECISIONS.md §DS-3.
 *
 * Everything the owner reads on screen follows the setting, via `formatDate`.
 */
export function formatDateUnambiguous(
  iso: string | null | undefined,
  fallback = '',
): string {
  const d = parseISODateOnly(iso);
  if (!d) return fallback;
  return formatDateTokens(d, UNAMBIGUOUS_TOKEN);
}

/**
 * Rewrite every bare ISO date inside a server-rendered SENTENCE.
 *
 * Most dates reach the screen as their own value and go through `formatDate`. A
 * few arrive already embedded in prose the backend composed — the concerning
 * daily-log callout is the one that matters: "On 2026-08-03, you logged that Mavis
 * refused food…". That string is shared by the dashboard and the vet prep sheet,
 * and it reached both with the date still in ISO, so it was the single date on an
 * otherwise written-month vet sheet (finding 11) and the single ISO date on a
 * dashboard belonging to an owner who had chosen written-month.
 *
 * `render` decides which way: the dashboard passes `formatDate` (the owner's
 * setting), the sheet passes `formatDateUnambiguous` (DS-3's stated exception).
 * Applying it where the sentence is DISPLAYED, rather than where it is composed,
 * is what lets one string serve both surfaces correctly.
 */
export function formatDatesWithin(
  text: string | null | undefined,
  render: (iso: string) => string,
): string {
  if (!text) return text ?? '';
  return text.replace(/\d{4}-\d{2}-\d{2}/g, (iso) => render(iso) || iso);
}

/** Today's date as an ISO `yyyy-MM-dd` string in the user's local zone. */
export function todayISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * What a date field MEANS, which is what its selectable year range should follow.
 * Setting the range per screen is how one field came to offer 1996–2026 while the
 * field beside it offered 2001–2031, and how a Care Pass became impossible to
 * backdate by a single day.
 *
 *  - 'happened'  — a record of something that already occurred: the past, not the
 *                  future. Weights, labs, vet visits, glucose readings, fluids.
 *  - 'lifetime'  — as above but reaching back over a whole cat's life: date of
 *                  birth, date of diagnosis.
 *  - 'scheduled' — a window being planned. Reaches into the future, and a little
 *                  way into the past, because a Care Pass is often written up the
 *                  morning after the caregiver actually started.
 */
export type DateRangeMeaning = 'happened' | 'lifetime' | 'scheduled';

const RANGE_YEARS_BACK: Record<DateRangeMeaning, number> = {
  happened: 20,
  lifetime: 30,
  scheduled: 0,
};

export interface DateBounds {
  min: string;
  max: string;
}

/** The ISO min/max a `DateRangeMeaning` implies, resolved against today. */
export function boundsFor(meaning: DateRangeMeaning): DateBounds {
  const now = new Date();
  if (meaning === 'scheduled') {
    const min = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    const max = new Date(now.getFullYear() + 2, now.getMonth(), now.getDate());
    return { min: format(min, 'yyyy-MM-dd'), max: format(max, 'yyyy-MM-dd') };
  }
  const min = new Date(
    now.getFullYear() - RANGE_YEARS_BACK[meaning],
    now.getMonth(),
    now.getDate(),
  );
  return { min: format(min, 'yyyy-MM-dd'), max: todayISO() };
}

/**
 * React hook that returns the current preference and re-renders on change.
 * Prefer this in components that must reflect a preference change without a
 * navigation (module-level `formatDate` reads a snapshot and only refreshes on
 * the next render).
 */
export function useDateFormatPref(): DateFormatPref {
  return useAuthStore((state) => {
    const pref = state.user?.date_format;
    return isDateFormatPref(pref) ? pref : DEFAULT_DATE_FORMAT;
  });
}
